import React from 'react';
import { StyledEngineProvider } from "@mui/material";
import { Provider } from 'react-redux'
import { configureStore } from '@reduxjs/toolkit'
import rootReducer from './reducers'
import { createRoot } from 'react-dom/client';

import './index.css';
import App from './App';

import { createTheme, ThemeProvider } from '@mui/material/styles'
import { lightBlue, blue } from '@mui/material/colors'

const theme = createTheme({
    palette: {
      primary: blue,
      secondary: lightBlue
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    maxWidth: '950px',
                    backgroundColor: '#e2e2e2',                
                    borderRadius: '12px',
                }
            }
        },

        MuiFormControlLabel: {
            styleOverrides: {
                root: {
                    paddingLeft: '24px',
                    PaddingRight: '18px'
                }
            }
        },

        MuiFormLabel: {
            styleOverrides: {
                root: {
                    "&.Mui-focused": {
                        color: '#3440E4'
                    },
                    "&.Mui-error": {
                        color: '#9C2F36'
                    },
                    '&.Mui-disabled': {
                        color:'#525252'
                    },            
                },
                asterisk: {
                    "&.Mui-error": {
                        color: '#9C2F36'
                    }
                }
            }
        },

        MuiInput: {
            styleOverrides: {
                underline: {
                    "&.Mui-error": {
                        '&:after': {
                       borderBottomColor: '#9C2F36'
                    }
                }
                }
            }  
        },

        MuiFormHelperText: {
            styleOverrides: {
                root: {  
                    color: '#9C2F36',   
                    "&.Mui-error": {
                        color: '#9C2F36'
                    }
                }
            }
        },

        MuiButton: {
            styleOverrides: {
                outlinedSecondary:{
                    color: '#3939C8',
                    borderColor: '#3939C8'
                },
                
                containedPrimary: {
                    backgroundColor: '#3939C8',
                    "&:hover": {
                        backgroundColor: '#1976d2',
                    }
                },
    
                textSecondary: {
                    color: '#0028FF'
                }
              },
        },

        MuiAccordion: {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    margin: '8px 0',
                    '&:before': {
                        display: 'none'
                    },
                    '&.Mui-disabled': {
                        backgroundColor: '#ababab',
                        opacity: '1.00'    
                    }       
                },
                rounded: {
                    '&:first-of-type': {
                        borderRadius: '12px',
                    },
                    '&:last-of-type': {
                        borderRadius: '12px',
                    },
                },
            }
        },

        MuiAccordionDetails: {
            styleOverrides: {
                root: {
                    display: 'block',
                    justifyContent: 'center'
                }
            }
        },
        
        MuiAccordionSummary: {
            styleOverrides: {      
                root: {               
                    '&.Mui-disabled': {
                        opacity: '1.00'     
                    }               
                },
            },
        },

        MuiStepper: {
            styleOverrides: {
                root: {
                    padding: '12px'
                }
            }
        },

        MuiIconButton: {
            styleOverrides: {
                root: {
                    padding: '1px'
                }
            }
        },
        MuiStepIcon: {
            styleOverrides: {
                root: {
                    color: 'lightBlue',
                    '&.Mui-active': {
                        color: '#1769aa',
                    },
                    '&.Mui-completed': {
                        color: 'green',
                    },
                }, 
                }         
        },

        MuiStepLabel: {
            styleOverrides: {
                iconContainer: {
                    transform: 'scale(1.15)',
                },
                label: {
                    color: '#3440E4',
                    fontSize: '0.65rem',
                    fontWeight: '550',
                    '&.Mui-active': {
                        color: '#ffffff',
                        backgroundColor: '#1769aa',
                        fontSize: '0.75rem',
                        fontWeight: '580',
                        borderRadius: '100px',
                    },
                    '&.Mui-completed': {
                        color: 'green',
                        fontSize: '0.65rem',
                        fontWeight: '550',
                    },               
                }
            }
        },

        MuiStepConnector: {  
            styleOverrides: {         
                horizontal: {
                    // left: 'calc(-50% + 20px)'
                },
                lineHorizontal: {
                    // width: '100%',
                    borderTopWidth: '4px',
                    // left: 'calc(-50% + 0px)'
                },
                alternativeLabel: {
                    left: 'calc(-50% + 13px)',
                    right: 'calc(50% + 13px)',
                    // '&:first-child': {
                    //     left: 'calc(-50% + 9px)',
                    //     right: 'calc(50% + 9px)',
                    // },
                    '&:nth-of-type(5)': {
                        left: 'calc(-50% + 7px)',
                        right: 'calc(50% + 7px)',
                    }
                },
                line: {
                    borderColor: 'lightBlue',
                },     
                active: {
                    '& .MuiStepConnector-line': {
                        borderColor: '#1769aa',
                    }
                },
                completed: {
                    '& .MuiStepConnector-line': {
                        borderColor: 'green',
                    }
                }
            }
        },
        MuiCheckbox: {
            styleOverrides: {
                colorSecondary: {
                  "&.Mui-checked": {
                    color: '#0040FF'
                }
              }
            }
        },
        MuiTabs: {
            styleOverrides: {
                indicator: {
                    backgroundColor: 'transparent'
                }
            }
        },
        MuiPickersToolbar: {
            styleOverrides: {
                root: {
                    backgroundColor: '#3939C8',
                }
            }
        },
        MuiPopover: {
            styleOverrides: {
                paper: {
                    marginLeft: '20px',
                    maxWidth: 'calc(100% - 70px)',
                    minWidth: '16px'
                }
            }
        }
    }
})

const store = configureStore({ 
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
        serializableCheck: false,
    }),
 })
const root = createRoot(document.getElementById('root'));

root.render(
    <React.StrictMode>
        <StyledEngineProvider injectFirst>
            <Provider store={store}>
                <ThemeProvider theme={theme}>
                        <App />
            </ThemeProvider>
        </Provider>
    </StyledEngineProvider>
  </React.StrictMode>
  );
